import {
  FileCode,
  FileJson,
  FileSpreadsheet,
  FileText,
  Presentation,
  Sheet,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/infrastructure/lib/utils";
import { isDocumentKind, type DocumentKind } from "@/shared/utils/documentFileTypes";

// Um ícone e uma cor por formato, no tom que o usuário associa a ele (PDF
// vermelho, Word azul, PowerPoint laranja, Excel verde). O tile usa a mesma cor
// em fundo translúcido, então todos os tipos têm a mesma forma e o mesmo peso.
const ICONS: Record<DocumentKind, { icon: LucideIcon; color: string; tile: string }> = {
  pdf: {
    icon: FileText,
    color: "text-red-600 dark:text-red-400",
    tile: "bg-red-500/10 border-red-500/20",
  },
  word: {
    icon: FileText,
    color: "text-blue-600 dark:text-blue-400",
    tile: "bg-blue-500/10 border-blue-500/20",
  },
  powerpoint: {
    icon: Presentation,
    color: "text-orange-600 dark:text-orange-400",
    tile: "bg-orange-500/10 border-orange-500/20",
  },
  excel: {
    icon: FileSpreadsheet,
    color: "text-green-600 dark:text-green-400",
    tile: "bg-green-500/10 border-green-500/20",
  },
  csv: {
    icon: Sheet,
    color: "text-teal-600 dark:text-teal-400",
    tile: "bg-teal-500/10 border-teal-500/20",
  },
  json: {
    icon: FileJson,
    color: "text-amber-600 dark:text-amber-400",
    tile: "bg-amber-500/10 border-amber-500/20",
  },
  markdown: {
    icon: FileCode,
    color: "text-violet-600 dark:text-violet-400",
    tile: "bg-violet-500/10 border-violet-500/20",
  },
};

const TILE_SIZES = {
  sm: { box: "h-7 w-7 rounded-md", icon: "h-4 w-4" },
  md: { box: "h-10 w-10 rounded-lg", icon: "h-5 w-5" },
} as const;

interface FileTypeIconProps {
  /** Tipo gravado em `storage.type`; valores fora do catálogo caem no ícone genérico. */
  kind?: string | null;
  /** Com `tile`, o ícone vem dentro de um quadrado colorido do tamanho escolhido. */
  tile?: keyof typeof TILE_SIZES;
  className?: string;
}

/**
 * PBI 87080 — ícone de um documento da Storage, derivado do formato. Vale para
 * todo documento cujo `type` está no catálogo — enviado ou gerado por agente —,
 * para a listagem, o detalhe e o formulário falarem a mesma língua visual.
 */
export function FileTypeIcon({ kind, tile, className }: FileTypeIconProps) {
  const entry = isDocumentKind(kind) ? ICONS[kind] : undefined;
  const Icon = entry?.icon ?? FileText;
  const color = entry?.color ?? "text-muted-foreground";

  if (!tile) {
    return <Icon aria-hidden className={cn(color, className)} />;
  }

  const size = TILE_SIZES[tile];
  return (
    <span
      aria-hidden
      className={cn(
        "inline-flex shrink-0 items-center justify-center border",
        size.box,
        entry?.tile ?? "bg-muted border-border",
        className,
      )}
    >
      <Icon className={cn(size.icon, color)} />
    </span>
  );
}
